import React from 'react'
import type { GroupResult } from '../../types'
import PyramidVisualization from './PyramidVisualization'
import RadarVisualization from './RadarVisualization'
import GroupedBreakdown from './GroupedBreakdown'

import { getUIStrings } from '../../i18n/ui'

interface ResultVisualizationProps {
  resultType: string
  groupResults?: GroupResult[]
  overallPercentage?: number
  score?: number
  scoreBreakdown?: Record<string, number>
  questionMap?: Record<string, string>
  lang?: string
}

const FlatScoreDisplay: React.FC<{
  score?: number
  scoreBreakdown?: Record<string, number>
  questionMap?: Record<string, string>
  lang?: string
}> = ({ score, scoreBreakdown, questionMap, lang }) => {
  if (score == null) return null

  const t = getUIStrings(lang)
  return (
    <div className="space-y-4">
      <div className="text-center">
        <span className="text-3xl font-bold text-primary-600">{score}</span>
        <span className="ml-2 text-foreground-muted">{t.points}</span>
      </div>
      {scoreBreakdown && Object.keys(scoreBreakdown).length > 0 && (
        <div className="space-y-2">
          {Object.entries(scoreBreakdown).map(([qId, pts]) => (
            <div key={qId} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
              <span className="text-foreground-muted">{questionMap?.[qId] || qId}</span>
              <span className="font-medium">{pts} {t.pts}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ResultVisualization: React.FC<ResultVisualizationProps> = ({
  resultType,
  groupResults,
  overallPercentage,
  score,
  scoreBreakdown,
  questionMap,
  lang,
}) => {
  // If we have group results, show the appropriate visualization
  if (groupResults && groupResults.length > 0 && overallPercentage != null) {
    switch (resultType) {
      case 'pyramid':
        return <PyramidVisualization groups={groupResults} overallPercentage={overallPercentage} lang={lang} />
      case 'breakdown':
        return <GroupedBreakdown groups={groupResults} overallPercentage={overallPercentage} lang={lang} />
      case 'radar':
        return <RadarVisualization groups={groupResults} overallPercentage={overallPercentage} lang={lang} />
      default:
        return <GroupedBreakdown groups={groupResults} overallPercentage={overallPercentage} lang={lang} />
    }
  }

  // Flat survey: show simple score display
  return <FlatScoreDisplay score={score} scoreBreakdown={scoreBreakdown} questionMap={questionMap} lang={lang} />
}

export default ResultVisualization
